import * as React from 'react';
import MyTypes from 'MyTypes';
import * as router from '../../../services/router-service'; 
import { connect, ConnectedProps } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { 
    Row, Col, Card, CardBody, CardTitle, CardSubtitle, 
    CardText, Button, Badge, Input, InputGroup 
} from 'reactstrap';
import { FaRobot, FaSearch, FaArrowRight, FaClock, FaEdit, FaHistory, FaTrash } from 'react-icons/fa';
import { aiLabActions } from 'store/aiLab';
import * as models from 'models';
import AiExecutionModal from 'components/ai-lab/AiExecutionModal';
import AiToolHistoryModal from '../AiToolHistoryModal';
import ConfirmModal from 'components/app/ConfirmModal';
import { userHasPermission } from '../../../utils/helpers';

interface CustomProps {
    history?: {
        push: (path: string) => void;
    };
}

interface CombinedProps extends PropsFromRedux, CustomProps {}

interface State {
    searchQuery: string;
    selectedLaunchToolId: number | null;
    selectedHistoryToolId: number | null;
    includeUnpublished: boolean;
    pendingDeleteTool: models.AiTool | null;
}

class AICatalogContainer extends React.PureComponent<CombinedProps, State> {
    public state: State = {
        searchQuery: "",
        selectedLaunchToolId: null,
        selectedHistoryToolId: null, 
        includeUnpublished: false,
        pendingDeleteTool: null
    };

    private toggleDeleteModal = () => {
        this.setState({ pendingDeleteTool: null });
    };

    private handleDeleteConfirm = () => {
        const { pendingDeleteTool } = this.state;
        if (pendingDeleteTool) {
            this.props.requestDeleteAiTool(pendingDeleteTool.id, pendingDeleteTool.name);
        }
    };

    public componentDidMount() {
        if (this.props.requestFetchCatalogTools) {
            this.props.requestFetchCatalogTools();
        }
    }

    private handleLaunchTool = (toolId: number) => {
        this.setState({ selectedLaunchToolId: toolId });
    };

    private handleCloseExecution = () => {
        this.setState({ selectedLaunchToolId: null });
    };

    private handleViewHistory = (toolId: number) => {
        this.setState({ selectedHistoryToolId: toolId });
    };

    private handleCloseHistory = () => {
        this.setState({ selectedHistoryToolId: null });
    };

    // Dynamically pushes the user to the deep-linked address bar route
    private handleEditRoute = (toolId: number) => {
        router.goto(`/ai-lab/designer/${toolId}`);
    };

    private handleExecutionSuccess = () => {
        if (this.props.requestFetchCatalogTools) {
            this.props.requestFetchCatalogTools(); // Pull down the fresh tool model states with new latestLogIds
        }
    };

    public render() {
        const { catalogTools, isLoading } = this.props;
        const { includeUnpublished, searchQuery, selectedLaunchToolId, selectedHistoryToolId, pendingDeleteTool } = this.state;
        const canDelete = userHasPermission("AI System Designer");

        const toolsArray = catalogTools?.resourceList || models.defaultResponse.resourceList; 
        const filteredTools = toolsArray.filter((tool: models.AiTool) => {
            if (!includeUnpublished && !tool.isPublished) {
                return false;
            }

            const query = searchQuery.toLowerCase().trim();
            if (!query) return true;
            
            return (
                (tool.name && tool.name.toLowerCase().includes(query)) ||
                (tool.description && tool.description.toLowerCase().includes(query))
            );
        });

        return (
            <div className="p-4">                
                {/* Search and Header Section */}
                <Row className="mb-4 align-items-center">
                    <Col md={6}>
                        <h4 className="fw-bold text-dark">AI Tool Catalog</h4>
                        <p className="text-muted small mb-0">Select and execute enterprise pipeline logic bundles globally.</p>
                    </Col>
                    <Col md={6}>
                        {/* PUBLISHED TOGGLE */}
                        <div className="d-flex align-items-center justify-content-end mb-2">
                            <label className="small text-muted fw-bold mb-0 me-2">
                                Show Unpublished
                            </label>
                            <div className="form-check form-switch">
                                <input 
                                    className="form-check-input" 
                                    type="checkbox" 
                                    checked={this.state.includeUnpublished}
                                    onChange={(e) => this.setState({ includeUnpublished: e.target.checked })}
                                />
                            </div>
                        </div>
                        
                        {/* EXISTING SEARCH INPUTGROUP */}
                        <InputGroup className="shadow-sm">
                            <div className="input-group-prepend">
                                <span className="input-group-text bg-white border-right-0">
                                    <FaSearch className="text-muted" />
                                </span>
                            </div>
                            <Input 
                                placeholder="Search available tools by name, description, or category..." 
                                className="border-left-0"
                                value={searchQuery}
                                onChange={(e) => this.setState({ searchQuery: e.target.value })}
                            />
                        </InputGroup>
                    </Col>
                </Row>

                {/* Loading State */}
                {isLoading && catalogTools?.totalCount === 0 && (
                    <div className="text-center py-5">
                        <div className="spinner-border text-success" role="status" />
                        <h6 className="mt-3 text-muted fw-bold">Hydrating Catalog Ecosystem...</h6>
                    </div>
                )}

                {/* Empty State Layout */}
                {!isLoading && filteredTools.length === 0 && (
                    <div className="text-center bg-white p-5 rounded border shadow-sm my-4">
                        <FaRobot size={48} className="text-muted mb-3" />
                        <h6 className="fw-bold text-secondary">No AI Tools Match Your Search Criteria</h6>
                        <p className="small text-muted mb-0">Try updating your filters or publish a new tool inside the AI Designer workspace.</p>
                    </div>
                )}

                {/* Tools Grid Layout */}
                <Row>
                    {filteredTools.map((tool: models.AiTool) => (
                        <Col key={tool.id} lg={4} md={6} className="mb-4">
                            <Card className="h-100 shadow-sm border rounded-lg transition-hover bg-white">
                                <CardBody className="d-flex flex-column p-4">
                                    <div className="d-flex justify-content-between align-items-center mb-3">                                        
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="p-2 bg-light rounded text-success d-inline-flex align-items-center justify-content-center bg-transparent">
                                                <FaRobot size={22} />                                          
                                            </div>
                                            
                                            {!tool.isPublished && (
                                                <Badge color="warning" className="text-dark" title="Never published. Test it in the designer sandbox, then publish it to run it here.">
                                                    DRAFT
                                                </Badge>
                                            )}
                                            {tool.hasPendingDraft && (
                                                <Badge color="info" className="text-white" title={`Runs use the published ${tool.versionNumber}. Unpublished edits are saved as v${tool.draftVersionNumber}.`}>
                                                    EDITS IN PROGRESS
                                                </Badge>
                                            )}
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            {/* Action: Route to designer URL */}
                                            <Button 
                                                color="secondary" 
                                                size="sm" 
                                                className="mr-1 py-1 px-2 text-white fw-bold shadow-sm d-inline-flex align-items-center justify-content-center"
                                                onClick={() => this.handleEditRoute(tool.id)}
                                                title="Edit tool configuration"
                                                style={{ color: '#fff' }}
                                            >
                                                <FaEdit size={12} className="me-1" style={{ marginRight: '4px' }} /> EDIT
                                            </Button>
                                            
                                            {tool.latestLogId ? (
                                                <Button 
                                                    color="info" 
                                                    size="sm" 
                                                    className="py-1 px-2 text-white fw-bold shadow-sm d-inline-flex align-items-center justify-content-center"
                                                    onClick={() => this.handleViewHistory(tool.id)}
                                                    title="View analysis execution logs"
                                                    style={{ color: '#fff' }}
                                                >
                                                    <FaHistory size={12} className="me-1" style={{ marginRight: '4px' }} /> LOGS
                                                </Button>
                                            ) : null}

                                            {canDelete && (
                                                <Button
                                                    color="danger"
                                                    size="sm"
                                                    className="py-1 px-2 text-white shadow-sm d-inline-flex align-items-center justify-content-center"
                                                    onClick={() => this.setState({ pendingDeleteTool: tool })}
                                                    title="Delete tool"
                                                    aria-label={`Delete ${tool.name}`}
                                                    style={{ color: '#fff', backgroundColor: '#dc3545', borderColor: '#dc3545' }}
                                                >
                                                    <FaTrash size={11} />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <CardTitle tag="h5" className="fw-bold text-dark mb-1">{tool.name}</CardTitle>
                                    <CardSubtitle className="mb-3 text-muted small">
                                        {tool.versionNumber || 'v1.0'}
                                    </CardSubtitle>
                                    
                                    <CardText className="text-secondary small flex-grow-1 line-clamp-3">
                                        {tool.description}
                                    </CardText>

                                    <hr className="my-3" />

                                    <div className="d-flex justify-content-between align-items-center mt-auto">
                                        <div className="text-muted small d-flex align-items-center">
                                            <FaClock className="text-muted" style={{ marginRight: '4px' }} /> {tool.lastUpdatedDate}
                                        </div>
                                        
                                        <Button 
                                            color="success" 
                                            size="sm" 
                                            className="fw-bold px-3 d-inline-flex align-items-center justify-content-center text-white"
                                            onClick={() => this.handleLaunchTool(tool.id)}
                                            disabled={!tool.isPublished}
                                            title={tool.isPublished ? undefined : "Publish this tool before running it. Use the designer sandbox to test drafts."}
                                        >
                                            LAUNCH <FaArrowRight style={{ marginLeft: '6px' }} size={11} />
                                        </Button>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    ))}
                </Row>

                <ConfirmModal
                    isOpen={pendingDeleteTool !== null}
                    toggle={this.toggleDeleteModal}
                    onConfirm={this.handleDeleteConfirm}
                    title="Delete AI Tool"
                    message={`"${pendingDeleteTool?.name ?? ''}" will be removed from the catalog and claim pages, and its name can be reused. Execution history is kept.`}
                    confirmText="Delete Tool"
                />

                {/* Inline Modal Portals Mapping */}
                {selectedLaunchToolId !== null && (
                    <AiExecutionModal
                        toolId={selectedLaunchToolId}
                        toolName={toolsArray.find((t: models.AiTool) => t.id === selectedLaunchToolId)?.name}
                        claimId={0} 
                        onClose={this.handleCloseExecution} 
                        onExecutionSuccess={this.handleExecutionSuccess}
                    />
                )}

                {selectedHistoryToolId !== null && (
                    <AiToolHistoryModal 
                        toolId={selectedHistoryToolId}
                        toolName={toolsArray.find((t: any) => t.id === selectedHistoryToolId)?.name}
                        onClose={this.handleCloseHistory} 
                    />
                )}
            </div>
        );
    }
}

const mapStateToProps = (state: MyTypes.RootState) => ({
    catalogTools: state.aiLab.aiLab.catalogTools,
    isLoading: state.aiLab.aiLab.isLoadingCatalog,
    errorMessage: state.aiLab.aiLab.catalogError
});

const mapDispatchToProps = (dispatch: Dispatch) =>
    bindActionCreators(
        {
            requestFetchCatalogTools: aiLabActions.requestFetchCatalogTools,
            requestDeleteAiTool: aiLabActions.requestDeleteAiTool
        },
        dispatch
    );

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(AICatalogContainer);